import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import { CloudDownload as CloudDownloadIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

import { useNodeImport } from '../hooks/useNodeImport';
import { nodeImportService } from '../services/nodeImport.service';
import { nodeEditorService } from '../services/nodeEditorService';
import { NodeImportDialog } from '../components/node-import/NodeImportDialog';
import type { ImportedNodeType } from '../types/nodeImport.types';

export const ImportedNodesPage: React.FC = () => {
  const navigate = useNavigate();
  const { nodes, count, status, loading, error, refresh } = useNodeImport();
  const [importOpen, setImportOpen] = useState(false);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleteAllBusy, setDeleteAllBusy] = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState('');

  // Table state
  const [page, setPage] = useState(0); // MUI is 0-based; DRF is 1-based
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<string>(''); // '' | 'active' | 'inactive'
  const [issuesFilter, setIssuesFilter] = useState<string>(''); // '' | 'with' | 'without'

  const summary = useMemo(() => {
    return {
      total: status?.total_imported_nodes ?? nodes.length,
      active: status?.active_nodes ?? nodes.filter((n) => n.is_active).length,
      withIssues: status?.nodes_with_errors ?? nodes.filter((n) => (n.validation_errors || []).length > 0).length,
    };
  }, [nodes, status]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const n of nodes) set.add(n.category || '');
    return Array.from(set).filter(Boolean).sort();
  }, [nodes]);

  const fetchPage = async () => {
    await refresh({
      page: page + 1,
      page_size: pageSize,
      search: search.trim() || undefined,
      category: category || undefined,
      is_active: activeFilter === '' ? undefined : activeFilter === 'active',
      has_errors: issuesFilter === '' ? undefined : issuesFilter === 'with',
    });
  };

  const currentFilters = useMemo(() => {
    return {
      search: search.trim() || undefined,
      category: category || undefined,
      is_active: activeFilter === '' ? undefined : activeFilter === 'active',
      has_errors: issuesFilter === '' ? undefined : issuesFilter === 'with',
    };
  }, [search, category, activeFilter, issuesFilter]);
  const deleteAllConfirmOk = deleteAllConfirmText.trim().toLowerCase() === 'delete';
  const isUnfiltered =
    !currentFilters.search &&
    !currentFilters.category &&
    activeFilter === '' &&
    issuesFilter === '';

  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, category, activeFilter, issuesFilter]);

  const toggleActive = async (node: ImportedNodeType) => {
    try {
      if (node.is_active) {
        await nodeImportService.deactivateNode(node.id);
        enqueueSnackbar('Node deactivated', { variant: 'success' });
      } else {
        await nodeImportService.activateNode(node.id);
        enqueueSnackbar('Node activated', { variant: 'success' });
      }
      await fetchPage();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to update node', { variant: 'error' });
    }
  };

  const deleteNode = async (node: ImportedNodeType) => {
    try {
      await nodeImportService.deleteNode(node.id);
      enqueueSnackbar('Node deleted', { variant: 'success' });
      await fetchPage();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to delete node', { variant: 'error' });
    }
  };

  const openInNodeEditor = async (node: ImportedNodeType) => {
    try {
      const mySessions = await nodeEditorService.getMySessions();
      const existingSession = mySessions.find((session: any) => session.node === node.id);
      if (existingSession?.id) {
        navigate(`/app/node-editor/${existingSession.id}`);
        return;
      }

      const created = await nodeEditorService.createSession({
        node_id: node.id,
        node_name: node.name,
        node_display_name: node.display_name,
      });
      navigate(`/app/node-editor/${created.id}`);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to open node in editor', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Imported Nodes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage n8n node definitions imported into your organization.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<CloudDownloadIcon />}
          onClick={() => setImportOpen(true)}
        >
          Import Node
        </Button>
      </Stack>

      <NodeImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={async () => {
          // reset to first page and refresh with current filters
          setPage(0);
          await fetchPage();
        }}
      />

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Chip label={`Total: ${summary.total}`} />
        <Chip color="success" label={`Active: ${summary.active}`} />
        <Chip color={summary.withIssues > 0 ? 'warning' : 'default'} label={`With issues: ${summary.withIssues}`} />
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            label="Search"
            size="small"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(0);
                fetchPage();
              }
            }}
            helperText="Press Enter to search by name/type/description"
          />

          <FormControl size="small" sx={{ minWidth: 180 }} fullWidth>
            <InputLabel id="imported-nodes-category">Category</InputLabel>
            <Select
              labelId="imported-nodes-category"
              label="Category"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }} fullWidth>
            <InputLabel id="imported-nodes-active">Status</InputLabel>
            <Select
              labelId="imported-nodes-active"
              label="Status"
              value={activeFilter}
              onChange={(e) => {
                setActiveFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 170 }} fullWidth>
            <InputLabel id="imported-nodes-issues">Issues</InputLabel>
            <Select
              labelId="imported-nodes-issues"
              label="Issues"
              value={issuesFilter}
              onChange={(e) => {
                setIssuesFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="with">With issues</MenuItem>
              <MenuItem value="without">No issues</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            onClick={() => {
              setSearch('');
              setCategory('');
              setActiveFilter('');
              setIssuesFilter('');
              setPage(0);
              fetchPage();
            }}
          >
            Reset
          </Button>

          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            disabled={loading || deleteAllBusy || count === 0}
            onClick={() => {
              setDeleteAllConfirmText('');
              setDeleteAllOpen(true);
            }}
          >
            Delete filtered ({count})
          </Button>
        </Stack>
      </Paper>

      <Dialog
        open={deleteAllOpen}
        onClose={() => {
          if (deleteAllBusy) return;
          setDeleteAllOpen(false);
          setDeleteAllConfirmText('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete imported nodes</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            This will permanently delete <b>{count}</b> imported nodes{isUnfiltered ? ' (all nodes)' : ''}. This cannot be undone.
          </Alert>
          <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
            Current filters
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip label={`Search: ${currentFilters.search || '—'}`} />
            <Chip label={`Category: ${currentFilters.category || '—'}`} />
            <Chip label={`Status: ${activeFilter === '' ? '—' : activeFilter === 'active' ? 'Active' : 'Inactive'}`} />
            <Chip label={`Issues: ${issuesFilter === '' ? '—' : issuesFilter === 'with' ? 'With issues' : 'Without issues'}`} />
          </Box>
          <TextField
            label='Type "DELETE" to confirm'
            fullWidth
            size="small"
            sx={{ mt: 2 }}
            value={deleteAllConfirmText}
            onChange={(e) => setDeleteAllConfirmText(e.target.value)}
            disabled={deleteAllBusy}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteAllOpen(false);
              setDeleteAllConfirmText('');
            }}
            disabled={deleteAllBusy}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteAllBusy || !deleteAllConfirmOk}
            onClick={async () => {
              try {
                setDeleteAllBusy(true);
                const resp = await nodeImportService.deleteAllImportedNodes(currentFilters);
                enqueueSnackbar(`Deleted ${resp.deleted} imported nodes`, { variant: 'success' });
                setDeleteAllOpen(false);
                setDeleteAllConfirmText('');
                setPage(0);
                await refresh({ page: 1, page_size: pageSize, ...currentFilters });
              } catch (e: any) {
                enqueueSnackbar(e?.message || 'Failed to delete imported nodes', { variant: 'error' });
              } finally {
                setDeleteAllBusy(false);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {loading && (
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      {!loading && !error && (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><b>Name</b></TableCell>
                  <TableCell><b>Type</b></TableCell>
                  <TableCell><b>Category</b></TableCell>
                  <TableCell><b>Version</b></TableCell>
                  <TableCell><b>Status</b></TableCell>
                  <TableCell><b>Issues</b></TableCell>
                  <TableCell align="right"><b>Actions</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {nodes.map((n) => {
                  const errs = n.validation_errors || [];
                  const errorCount = errs.filter((e) => e.severity === 'error').length;
                  const warningCount = errs.filter((e) => e.severity === 'warning').length;
                  return (
                    <TableRow key={n.id} hover onDoubleClick={() => openInNodeEditor(n)} sx={{ cursor: 'pointer' }}>
                      <TableCell>
                        <Typography fontWeight="bold" variant="body2" noWrap title={n.display_name}>
                          {n.display_name}
                        </Typography>
                        {n.description && (
                          <Typography variant="caption" color="text.secondary" noWrap title={n.description}>
                            {n.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" noWrap title={n.name}>
                          {n.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={n.category} />
                      </TableCell>
                      <TableCell>v{n.version}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={n.is_active ? 'success' : 'default'}
                          label={n.is_active ? 'Active' : 'Inactive'}
                          onClick={() => toggleActive(n)}
                          variant={n.is_active ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        {errorCount > 0 && <Chip size="small" color="error" label={`${errorCount} error`} sx={{ mr: 0.5 }} />}
                        {warningCount > 0 && <Chip size="small" color="warning" label={`${warningCount} warn`} />}
                        {errorCount === 0 && warningCount === 0 && <Typography variant="caption" color="text.secondary">—</Typography>}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<EditIcon />}
                          onClick={() => openInNodeEditor(n)}
                          sx={{ mr: 1 }}
                        >
                          Open
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<DeleteIcon />}
                          onClick={() => deleteNode(n)}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {nodes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Box sx={{ p: 2 }}>
                        <Typography>No imported nodes found for your current filters.</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={count}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Paper>
      )}
    </Box>
  );
};


